import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { selectAgents, updateContact } from '@/redux/agentsSlice';
import { useContactSort } from '@/hooks/useContactSort';
import { useContactFilter } from '@/hooks/useContactFilter';
import { useToast } from '@/hooks/useToast';
import ContactsTable from '@/components/page-components/project/agents/contacts/ContactsTable';
import ContactActionBar from './ContactActionBar';
import type { Agent } from '@/types/Agents';
import type { AppDispatch } from '@/redux/store';

const Contacts: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  const { agent_id } = useParams<{ agent_id: string }>();

  // Get the current agent from Redux
  const agents = useSelector(selectAgents);
  const currentAgent = agents.find(
    (agent: Agent) => agent.id === Number(agent_id)
  );

  // Get contacts from the agent's contacts array in Redux and add computed fields
  const contacts = useMemo(() => {
    if (!currentAgent?.contacts || !currentAgent?.conversations) return [];

    const computedContacts = currentAgent.contacts.map(contact => {
      // Find conversations for this contact
      const contactConversations = currentAgent.conversations!.filter(
        conv => conv.caller_id === contact.caller_id
      );

      let lastConversationAt: string | undefined;
      if (contactConversations.length > 0) {
        const lastConversation = contactConversations.reduce(
          (latest, current) => {
            return new Date(current.created_at) > new Date(latest.created_at)
              ? current
              : latest;
          }
        );
        lastConversationAt = lastConversation.created_at;
      }

      return {
        ...contact,
        last_conversation_at: lastConversationAt,
        conversations_count: contactConversations.length,
      };
    });

    return computedContacts;
  }, [currentAgent?.contacts, currentAgent?.conversations]);

  const { sortedContacts, requestSort, sortConfig } = useContactSort(contacts);
  const { filteredContacts, searchQuery, setSearchQuery } =
    useContactFilter(sortedContacts);

  const handleUpdateContact = async (contactId: number, name: string) => {
    try {
      await dispatch(updateContact({ contactId, updates: { name } })).unwrap();

      // Show success toast
      toast({
        title: t('contacts.update_success.title', 'Contact Updated'),
        description: t(
          'contacts.update_success.description',
          'Contact name has been updated successfully.'
        ),
        variant: 'default',
      });
    } catch (error) {
      console.error('Failed to update contact:', error);

      // Show error toast
      toast({
        title: t('contacts.update_error.title', 'Update Failed'),
        description: t(
          'contacts.update_error.description',
          'Failed to update contact name. Please try again.'
        ),
        variant: 'destructive',
      });

      throw error;
    }
  };

  if (!currentAgent) return <div>{t('error.agent_not_found.translation')}</div>;

  return (
    <div className="flex flex-col gap-2 w-full space-y-2">
      <div className="flex flex-col md:flex-row md:items-center md:justify-end w-full gap-2">
        <ContactActionBar
          totalCount={contacts.length}
          filteredCount={filteredContacts.length}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </div>

      <ContactsTable
        contacts={filteredContacts}
        conversations={currentAgent?.conversations || []}
        agentId={Number(agent_id)}
        sortConfig={sortConfig}
        onSort={requestSort}
        onUpdateContact={handleUpdateContact}
      />
    </div>
  );
};

export default Contacts;
